// A spectral picture of a short excerpt, drawn here from the samples (no server work): short-time FFT,
// log-frequency axis from 40 Hz so a low thump and a high click both show, the spot marked, and a
// playhead while it plays.
import { useEffect, useMemo, useRef } from "react";
import type { Excerpt } from "../lib/wavRange";
import { playbackPosition } from "../lib/wavRange";

const N = 512;
const HOP = 64;
const FMIN = 40;
const RANGE_DB = 80;
// perceptual dark → violet → orange → pale ramp (close to "magma"), readable in both themes
const STOPS: [number, number, number][] = [[0, 0, 4], [40, 15, 100], [120, 30, 130], [215, 70, 105], [253, 150, 105], [252, 250, 190]];

function color(v: number): [number, number, number] {
  const x = Math.max(0, Math.min(1, v)) * (STOPS.length - 1);
  const i = Math.min(STOPS.length - 2, Math.floor(x));
  const f = x - i;
  const a = STOPS[i], b = STOPS[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

/** In-place radix-2 FFT (length a power of two). Shared with the Noise Analytics view. */
export function fft(re: Float64Array, im: Float64Array) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k, b = a + len / 2;
        const tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti;
        re[a] += tr; im[a] += ti;
        const ncr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = ncr;
      }
    }
  }
}

/** Frames × bins of dB, newest-first cached per excerpt. */
function stft(samples: Float32Array): { db: Float32Array[]; max: number } {
  const win = new Float64Array(N).map((_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1)));
  const out: Float32Array[] = [];
  let max = -200;
  const re = new Float64Array(N), im = new Float64Array(N);
  for (let s = 0; s + N <= samples.length; s += HOP) {
    for (let i = 0; i < N; i++) { re[i] = samples[s + i] * win[i]; im[i] = 0; }
    fft(re, im);
    const col = new Float32Array(N / 2);
    for (let k = 0; k < N / 2; k++) {
      const d = 10 * Math.log10(re[k] * re[k] + im[k] * im[k] + 1e-20);
      col[k] = d;
      if (d > max) max = d;
    }
    out.push(col);
  }
  return { db: out, max };
}

export function Spectrogram({ excerpt, playId, markT, markDur, label, height = 150, fmax = 16000, onPick, pending }: {
  excerpt: Excerpt | null;
  playId: string;            // matches the id given to playExcerpt, to draw the playhead
  markT: number | null;      // spot centre, seconds on this recording
  markDur: number;
  label: string;
  height?: number;
  fmax?: number;
  /** Click (a point) or drag (a span) on the picture: start time and length, seconds on this recording. */
  onPick?: (t: number, dur: number) => void;
  /** A point or span the reviewer has marked but not yet added (seconds on this recording). */
  pending?: { t: number; dur: number } | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x0: number; x1: number } | null>(null);
  const toTime = (x: number, w: number) => {
    if (!excerpt) return 0;
    const span = excerpt.samples.length / excerpt.sampleRate || 1;
    return excerpt.t0 + Math.max(0, Math.min(1, x / w)) * span;
  };
  const spec = useMemo(() => (excerpt && excerpt.samples.length >= N ? stft(excerpt.samples) : null), [excerpt]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const g = cv.getContext("2d");
    if (!g) return;
    if (!spec || !excerpt) {
      g.clearRect(0, 0, cv.width, cv.height);
      return;
    }
    const W = spec.db.length, H = height;
    cv.width = W;
    cv.height = H;
    const img = g.createImageData(W, H);
    const sr = excerpt.sampleRate;
    const top = Math.min(fmax, sr / 2);
    const binHz = sr / N;
    const lo = Math.log(FMIN), hi = Math.log(top);
    // for each pixel row, the bins it covers (max-pooled, so a thin click isn't lost)
    const rows: [number, number][] = [];
    for (let y = 0; y < H; y++) {
      const fA = Math.exp(lo + ((H - y - 1) / H) * (hi - lo));
      const fB = Math.exp(lo + ((H - y) / H) * (hi - lo));
      const kA = Math.max(0, Math.floor(fA / binHz)), kB = Math.min(N / 2 - 1, Math.max(kA, Math.ceil(fB / binHz)));
      rows.push([kA, kB]);
    }
    for (let x = 0; x < W; x++) {
      const col = spec.db[x];
      for (let y = 0; y < H; y++) {
        let m = -200;
        for (let k = rows[y][0]; k <= rows[y][1]; k++) if (col[k] > m) m = col[k];
        const [r, gg, b] = color((m - (spec.max - RANGE_DB)) / RANGE_DB);
        const o = (y * W + x) * 4;
        img.data[o] = r; img.data[o + 1] = gg; img.data[o + 2] = b; img.data[o + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
  }, [spec, excerpt, height, fmax]);

  // overlay: the spot, and the playhead (animated while this excerpt plays)
  useEffect(() => {
    const ov = overlayRef.current;
    if (!ov || !excerpt) return;
    let raf = 0;
    const draw = () => {
      const g = ov.getContext("2d");
      if (!g) return;
      const W = ov.clientWidth, H = ov.clientHeight;
      if (ov.width !== W) ov.width = W;
      if (ov.height !== H) ov.height = H;
      g.clearRect(0, 0, W, H);
      const span = excerpt.samples.length / excerpt.sampleRate || 1;
      if (markT != null) {
        const x0 = ((markT - markDur / 2 - excerpt.t0) / span) * W;
        const x1 = ((markT + markDur / 2 - excerpt.t0) / span) * W;
        const w = Math.max(6, x1 - x0);
        const cx = (x0 + x1) / 2;
        g.strokeStyle = "rgba(255,255,255,0.9)";
        g.lineWidth = 1.5;
        g.setLineDash([4, 3]);
        g.strokeRect(cx - w / 2, 1, w, H - 2);
        g.setLineDash([]);
      }
      // frequency guide on the log axis: 100 Hz, 1 kHz, 10 kHz
      const top = Math.min(fmax, excerpt.sampleRate / 2);
      g.font = "10px system-ui, sans-serif";
      g.textAlign = "right";
      for (const [f, txt] of [[100, "100 Hz"], [1000, "1 kHz"], [10000, "10 kHz"]] as [number, string][]) {
        if (f >= top) continue;
        const y = H - ((Math.log(f) - Math.log(FMIN)) / (Math.log(top) - Math.log(FMIN))) * H;
        g.fillStyle = "rgba(255,255,255,0.35)";
        g.fillRect(W - 34, y, 34, 1);
        g.fillStyle = "rgba(255,255,255,0.75)";
        g.fillText(txt, W - 4, y - 3);
      }
      // what the reviewer has marked (or is dragging): a bright line for a point, a box for a span
      const span2 = excerpt.samples.length / excerpt.sampleRate || 1;
      const mark = drag.current
        ? { a: Math.min(drag.current.x0, drag.current.x1), b: Math.max(drag.current.x0, drag.current.x1) }
        : pending ? { a: ((pending.t - excerpt.t0) / span2) * W, b: ((pending.t + pending.dur - excerpt.t0) / span2) * W } : null;
      if (mark) {
        g.fillStyle = "rgba(57, 208, 255, 0.22)";
        if (mark.b - mark.a >= 3) g.fillRect(mark.a, 0, mark.b - mark.a, H);
        g.strokeStyle = "#39d0ff";
        g.lineWidth = 3;
        g.beginPath(); g.moveTo(mark.a, 0); g.lineTo(mark.a, H);
        if (mark.b - mark.a >= 3) { g.moveTo(mark.b, 0); g.lineTo(mark.b, H); }
        g.stroke();
        const cx = (mark.a + mark.b) / 2;                 // a pointer at the top, easy to find on a busy picture
        g.fillStyle = "#39d0ff";
        g.beginPath(); g.moveTo(cx - 7, 0); g.lineTo(cx + 7, 0); g.lineTo(cx, 9); g.closePath(); g.fill();
      }
      const pos = playbackPosition(playId);
      if (pos != null) {
        const x = (pos / span) * W;
        g.strokeStyle = "#ffffff";
        g.lineWidth = 2;
        g.beginPath(); g.moveTo(x, 0); g.lineTo(x, H); g.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [excerpt, markT, markDur, playId, pending]);

  return (
    <div className="spec">
      <div className="spec-label">{label}</div>
      <div
        className={`spec-frame${onPick ? " pickable" : ""}`}
        style={{ height }}
        onPointerDown={(e) => {
          if (!onPick || !excerpt) return;
          const r = e.currentTarget.getBoundingClientRect();
          drag.current = { x0: e.clientX - r.left, x1: e.clientX - r.left };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          const r = e.currentTarget.getBoundingClientRect();
          drag.current.x1 = Math.max(0, Math.min(r.width, e.clientX - r.left));
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          drag.current = null;
          if (!d || !onPick) return;
          const w = e.currentTarget.getBoundingClientRect().width;
          const a = Math.min(d.x0, d.x1), b = Math.max(d.x0, d.x1);
          if (b - a < 4) onPick(toTime((a + b) / 2, w), 0);
          else onPick(toTime(a, w), toTime(b, w) - toTime(a, w));
        }}
        onPointerCancel={() => { drag.current = null; }}
      >
        <canvas ref={canvasRef} className="spec-canvas" />
        <canvas ref={overlayRef} className="spec-overlay" />
        {!excerpt && <div className="spec-loading">Loading…</div>}
      </div>
    </div>
  );
}
